import { Box as MuiBox } from '@mui/material';
import moment from 'moment';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AdditionalInterestAndInsured } from '../../configs/APISchemas/Application.schema';
import type { PurchasePolicyInput } from '../../redux/reducer/v2/purchasePolicy';
import { getPurchasePolicyInput } from '../../redux/selectors/purchasePolicy';
import { getCompanyVendor } from '../../redux/selectors/shared';
import {
  PaymentReviewDetails,
  PaymentReviewDetailsProps,
} from '../FormComponents/Components/PaymentReviewDetails';

const getBaseCoverages = (application: PurchasePolicyInput) => {
  const coverages = [
    {
      name: 'Property Coverage',
      cost: application.personalPropertyCoverage || 0,
    },
    {
      name: 'Personal Liability',
      cost: application.personalLiabilityCoverage || 0,
    },
    {
      name: 'Medical Liability',
      cost: application.medicalPayments || 0,
    },
    {
      name: 'Loss of Use',
      cost: application.lossOfUse || 0,
    },
    {
      name: 'Deductible',
      cost: application.deductible || 0,
    },
  ];

  if (application.theft) {
    coverages.push({
      name: 'Theft Deductible',
      cost: application.theft || 0,
    });
  }

  if (application.waterBackup) {
    coverages.push({
      name: 'Water Backup',
      cost: application.waterBackup || 0,
    });
  }

  if (application.petDamage) {
    coverages.push({
      name: 'Pet Damage',
      cost: application.petDamage || 0,
    });
  }

  if (application.identityFraud) {
    coverages.push({
      name: 'Identity Fraud',
      cost: application.identityFraud || 0,
    });
  }

  if (application.windHail) {
    coverages.push({
      name: 'Wind Hail Deductible',
      cost: application.windHail || 0,
    });
  }

  if (application.earthquake) {
    coverages.push({
      name: 'Earthquake',
      cost: application.earthquake || 0,
    });
  }

  if (application.hurricane) {
    coverages.push({
      name: 'Hurricane Deductible',
      cost: application.hurricane || 0,
    });
  }

  return coverages;
};

const formatAdditionalInsured = (
  additionalInsured: AdditionalInterestAndInsured[],
) => {
  return additionalInsured.map((person) => ({
    name: `${person.firstName || ''} ${person.lastName || ''}`.trim(),
    email: person.email,
  }));
};

const Sidebar: FC<{
  isSkipUnitNumber?: boolean;
}> = ({ isSkipUnitNumber }) => {
  const purchasePolicyInput = useSelector(getPurchasePolicyInput);
  const companyVendor = useSelector(getCompanyVendor);

  const detailInfo = useMemo<PaymentReviewDetailsProps>(() => {
    const {
      firstName,
      lastName,
      email,
      phone,
      address,
      buildingName,
      unit,
      buildingUnit,
      buildingAddress,
    } = purchasePolicyInput;

    const coverages = getBaseCoverages(purchasePolicyInput);

    return {
      id: '',
      COMPANY_VENDOR: companyVendor,
      propertyName: buildingName || '',
      address: address || buildingAddress?.value,
      buildingUnit: !isSkipUnitNumber ? buildingUnit || unit?.name : '',
      peopleInPolicy: firstName
        ? [
            {
              name: `${firstName} ${lastName ? lastName : ''}`,
              email,
              phone,
            },
          ]
        : [],
      forSideBar: true,
      coverageStartDate:
        purchasePolicyInput.coverageStart &&
        moment(purchasePolicyInput.coverageStart).format('MMMM Do YYYY'),
      coverages: purchasePolicyInput.personalPropertyCoverage
        ? coverages
        : undefined,
      additionalInsuredRoommates: formatAdditionalInsured(
        purchasePolicyInput.additionalInsuredRoommates,
      ),
      additionalInterests: formatAdditionalInsured(
        purchasePolicyInput.additionalInterests,
      ),
    };
  }, [isSkipUnitNumber, companyVendor, purchasePolicyInput]);

  return (
    <MuiBox marginLeft={3}>
      {detailInfo && <PaymentReviewDetails {...detailInfo} />}
    </MuiBox>
  );
};

export default Sidebar;
